/**
 * Data transformation functions for ClientInfoCard
 * Extracts and processes client data for display
 */

import type { Client, Assessment, PostureAnalysis } from '@/lib/types';
import type { ClientStatistics, ClassificationInfo } from './types';
import { computeShortClientClassification } from '@/components/clients/lib/client-classification';
import type { AnalysisData } from '@/components/posture-analysis/types';
import { safeToDate } from '@/lib/utils';
import logger from '@/lib/logging';

/**
 * Extract statistics from posture analyses and assessments
 */
export function extractClientStatistics(
  postureAnalyses: PostureAnalysis[],
  assessments: Assessment[]
): ClientStatistics {
  const postureCount = postureAnalyses.length;
  const assessmentCount = assessments.length;

  // Get last posture analysis date
  let postureLastDate: Date | null = null;
  if (postureAnalyses.length > 0) {
    const latest = postureAnalyses[0];
    postureLastDate = safeToDate(latest.createdAt) ?? null;
  }

  // Get last assessment date
  let assessmentLastDate: Date | null = null;
  if (assessments.length > 0) {
    const latest = assessments[0];
    assessmentLastDate = safeToDate(latest.date) || safeToDate(latest.createdAt);
  }

  return {
    postureCount,
    postureLastDate,
    assessmentCount,
    assessmentLastDate,
  };
}

/**
 * Get the last assessment date from assessments array
 */
export function getLastAssessmentDate(assessments: Assessment[]): Date | null {
  if (assessments.length === 0) return null;
  
  const latest = assessments[0];
  return safeToDate(latest.date) || safeToDate(latest.createdAt);
}

/**
 * Get the last posture analysis date from analyses array
 */
export function getLastPostureAnalysisDate(analyses: PostureAnalysis[]): Date | null {
  if (analyses.length === 0) return null;
  
  const latest = analyses[0];
  return safeToDate(latest.createdAt) ?? null;
}

/**
 * Format classification information from client data
 * Extracts classification from client's classification field and assessment data
 */
export async function formatClassification(
  client: Client,
  postureAnalyses: PostureAnalysis[],
  assessments: Assessment[]
): Promise<ClassificationInfo> {
  let posture: string | null = null;
  let sop: string | null = null;

  // Try to extract posture classification from most recent posture analysis
  if (postureAnalyses.length > 0) {
    try {
      const latestAnalysis = postureAnalyses[0];
      if (latestAnalysis.analysis && Object.keys(latestAnalysis.analysis).length > 0) {
        const analysisData = latestAnalysis.analysis as unknown as AnalysisData;
        const computedPosture = await computeShortClientClassification(analysisData);
        if (computedPosture && computedPosture !== 'N') {
          posture = computedPosture;
        }
      }
    } catch (error) {
      logger.warn('Error computing posture classification:', error);
    }
  }

  // Extract SOP classification from client's classification field
  // The classification field is typically computed from the most recent SOP assessment
  // If we have both posture and client classification, client classification is likely SOP-based
  if (client.classification && client.classification.trim()) {
    const classification = client.classification.trim();
    
    // If we have a posture classification, the client classification is likely SOP-based
    if (posture && classification !== posture) {
      sop = classification;
    } else if (!posture) {
      // No posture classification, client classification could be from either source
      // For display purposes, we'll show it as SOP classification
      sop = classification;
    }
  }

  // Format display text
  let displayText = 'No classification available';
  if (posture && sop) {
    displayText = `Posture: ${posture} | SOP: ${sop}`;
  } else if (posture) {
    displayText = `Posture: ${posture}`;
  } else if (sop) {
    displayText = `SOP: ${sop}`;
  }

  return {
    posture,
    sop,
    displayText,
  };
}

